import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Assistant service — thin wrapper around the `askAssistant` Cloud Function
 * (the "Senior špeditér" AI advisor). ADVISOR ONLY: it answers how-to questions
 * about using TRINIS and gives general freight-forwarding guidance; it never
 * performs actions.
 *
 * Mirrors the repo service convention (class-based singleton). It maps Firebase
 * callable error codes (`functions/<code>`) to i18n keys thrown as
 * `Error('assistant.errors.<key>')` so the UI translates them via `t(err.message)`
 * — never expose a raw error string to users.
 */

/** The 7 platform locales the assistant can reply in. */
export type AssistantLanguage = 'sk' | 'en' | 'cs' | 'pl' | 'de' | 'hu' | 'ro';

/** A single prior turn sent back as context (last ~8 kept by the server). */
export interface AssistantHistoryTurn {
  role: 'user' | 'assistant';
  text: string;
}

/** Per-company monthly usage returned with every successful answer. */
export interface AssistantUsage {
  used: number;
  limit: number;
  period: string;
}

/** Successful answer payload from `askAssistant`. */
export interface AssistantAnswer {
  answer: string;
  usage: AssistantUsage;
}

interface AskAssistantRequest {
  question: string;
  language?: AssistantLanguage;
  history?: AssistantHistoryTurn[];
}

/** i18n key thrown when the quota is exhausted — the UI keys the paywall off this. */
export const ASSISTANT_QUOTA_ERROR = 'assistant.errors.quota_exceeded';

/**
 * Map a Firebase callable error to an `assistant.errors.*` i18n key.
 * Firebase exposes the server `HttpsError` code as `functions/<code>` and the
 * server message string (e.g. 'question-too-long') as `error.message`.
 */
function mapAssistantError(err: unknown): string {
  const code = (err as { code?: string })?.code;
  const message = (err as { message?: string })?.message ?? '';

  switch (code) {
    case 'functions/unauthenticated':
      return 'assistant.errors.unauthenticated';
    case 'functions/permission-denied':
      return 'assistant.errors.no_company';
    case 'functions/invalid-argument':
      return message.includes('too-long')
        ? 'assistant.errors.too_long'
        : 'assistant.errors.empty';
    case 'functions/resource-exhausted':
      return ASSISTANT_QUOTA_ERROR;
    case 'functions/unavailable':
    case 'functions/not-found':
      // Function unreachable / not deployed yet — distinct, retryable state.
      return 'assistant.errors.unavailable';
    case 'functions/internal':
    default:
      return 'assistant.errors.failed';
  }
}

class AssistantService {
  /**
   * Ask the assistant a question. Resolves with the answer + current usage, or
   * rejects with an `Error` whose message is an `assistant.errors.*` i18n key.
   *
   * @param question  1–2000 chars (trimmed). Empty rejects locally.
   * @param language  one of the 7 platform locales; the reply is in this language.
   * @param history   prior turns for context — the server keeps only the last ~8.
   */
  async ask(
    question: string,
    language?: AssistantLanguage,
    history?: AssistantHistoryTurn[],
  ): Promise<AssistantAnswer> {
    const trimmed = (question || '').trim();
    if (!trimmed) {
      throw new Error('assistant.errors.empty');
    }
    if (trimmed.length > 2000) {
      throw new Error('assistant.errors.too_long');
    }

    try {
      const callable = httpsCallable<AskAssistantRequest, AssistantAnswer>(
        functions,
        'askAssistant',
      );
      const payload: AskAssistantRequest = { question: trimmed };
      if (language) payload.language = language;
      if (history && history.length) payload.history = history;

      const res = await callable(payload);
      return res.data;
    } catch (err) {
      throw new Error(mapAssistantError(err));
    }
  }
}

export const assistantService = new AssistantService();
export default assistantService;
